import {
  IDonation,
  IBoard,
  requestDonation,
  requestComment,
} from '@src/interfaces/community';
import { authKeys } from '@src/keys/authKey';
import { communityKeys } from '@src/keys/communityKey';
import { modalAtom } from '@src/recoils/atom/modal';
import { blood, community } from '@src/services/api';
import authApiList from '@src/services/auth';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useBodyScrollLock } from '@src/hooks/useBodyScrollLock';
import useToast from './useToast';
import { copyClipBoard } from '@src/utils/community';
import * as gtag from '@src/utils/gtag';
import useAuth from './useAuth';
import profileAtom from '@src/recoils/atom/profile';
import { BloodKeys } from '@src/keys/bloodKey';

export const fetchDonations = async ({
  pageParam = 0,
  keyword = '',
  abo = '',
  bloodProduct = '',
  sortBy = '',
}) => {
  const result: IDonation = await community.getDonations(
    pageParam,
    keyword,
    abo,
    bloodProduct,
    sortBy
  );
  return {
    data: result,
    nextPage: pageParam + 1,
  };
};
export const useDonations = (
  keyword?: string,
  abo?: string,
  bloodProduct?: string,
  sortBy?: string
) => {
  const {
    data: communitys,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    communityKeys.donationPage(keyword, abo, bloodProduct, sortBy),
    () => fetchDonations({ pageParam: 0, keyword, abo, bloodProduct, sortBy }),
    {
      getNextPageParam: (lastPage: any) => {
        if (lastPage.data.last == false) {
          return lastPage.nextPage;
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
    }
  );

  return {
    communitys,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

export const fetchBoards = async ({ pageParam = 0, keyword = '' }) => {
  const result: IBoard = await community.getBoards(pageParam, keyword);
  return {
    data: result,
    nextPage: result.last ? undefined : pageParam + 1,
  };
};

export const useBoards = (keyword?: string) => {
  const {
    data: communitys,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    communityKeys.boardPage(keyword),
    ({ pageParam = 0 }) => fetchBoards({ pageParam, keyword }),
    {
      getNextPageParam: (lastPage: any) => {
        if (lastPage.data.last == false) {
          return lastPage.nextPage;
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
    }
  );

  return {
    communitys,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

export const useSingleCommunity = (id: string) => {
  const fetchBoard = () => {
    return useQuery(
      communityKeys.detailPage(id),
      async () => {
        return await community.getBoard(id);
      },
      {
        retry: 1,
        refetchOnMount: true,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
      }
    );
  };

  const fetchDonation = () => {
    return useQuery(
      communityKeys.detailPage(id),
      async () => {
        return await community.getDonation(id);
      },
      {
        retry: 1,
        refetchOnMount: true,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
      }
    );
  };

  const fetchShareDonation = () => {
    return useQuery(
      communityKeys.detailPage(id),
      async () => {
        return await community.encodedGetDonation(id);
      },
      {
        retry: 1,
        refetchOnMount: true,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
      }
    );
  };

  return {
    fetchBoard,
    fetchDonation,
    fetchShareDonation,
  };
};

export const useCommunityListMutaion = (communityType: string) => {
  const setModal = useSetRecoilState(modalAtom);
  const { lockScroll } = useBodyScrollLock();
  const queryClient = useQueryClient();
  const profile = useRecoilValue(profileAtom);
  // 프로필 조회를 이미해주기 때문에 캐싱된 데이터를 가져와서 비교한다.
  return useMutation(
    async (id: string) => {
      // 프로필 조회로 얻은 데이터가 없을시 로그인 X 유저이기때문에 리턴해서 호출을 방지한다.
      if (!profile.isLoggedIn) {
        lockScroll();
        setModal({
          id: 'unAuthorized',
          visible: true,
        });
        return;
      }
      await community.toggleLike(id, communityType);
      // TODO : 좋아요 하트만 칠해주고 조회는 하지않는다.
      if (communityType === 'donation') {
        queryClient.invalidateQueries(['donation-page']);
        gtag.like(communityType);
      } else {
        queryClient.invalidateQueries(['board-page']);
        gtag.like('board');
      }
    },
    {
      onError: () => {
        alert('좋아요 에러');
      },
    }
  );
};

export const useCommunitySingleMutaion = (
  communityType: string,
  id: string
) => {
  const queryClient = useQueryClient();
  const setModal = useSetRecoilState(modalAtom);
  const { lockScroll } = useBodyScrollLock();
  const profile = useRecoilValue(profileAtom);
  return useMutation(
    async (uuId: string) => {
      if (!profile.isLoggedIn) {
        lockScroll();
        setModal({
          id: 'unAuthorized',
          visible: true,
        });
        return;
      }
      await community.toggleLike(uuId, communityType);
      // TODO: 좋아요 색칠만 하고 조회 ㄴㄴ
      if (communityType === 'donation') {
        queryClient.invalidateQueries(communityKeys.detailPage(id));
        gtag.like(communityType);
      } else {
        queryClient.invalidateQueries(communityKeys.detailPage(id));
        gtag.like('board');
      }
    },
    {
      onError: () => {
        alert('좋아요 에러');
      },
    }
  );
};
/**
 * post 글 생성 hooks
 * TODO : 글 수정 hooks도 분리한다.
 */
export const useInsertCommunity = (communityType: string) => {
  const router = useRouter();
  if (communityType === 'donation') {
    return useMutation(async (donationData: any) => {
      const res: any = await community.insertDonation(donationData);
      if (res.status === 200) {
        gtag.checkCommunityInsert(communityType);
        router.push('/community/donation');
      } else {
        alert('작성을 실패했습니다.');
      }
    });
  } else {
    return useMutation(async (boardData: any) => {
      const res: any = await community.insertBoard(boardData);
      if (res.status === 200) {
        gtag.checkCommunityInsert('board');
        router.push('/community/board');
      } else {
        alert('작성을 실패했습니다.');
      }
    });
  }
};

// 피플사연과 피플생활 댓글, 대댓글 둘다 관리하는 hooks
export const useInsertComment = (id: string, communityType: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (body: requestComment) => {
      // TODO 뮤테이션 콜백함수에 인자가 두개이상 들어갈 수 있으면 개선한다.
      if (body.isReply) {
        const data = await community.insertReplyComment(body, communityType);
        if (data.status === 200) {
          gtag.checkCommunityCommentInsert(body.isReply);
          queryClient.invalidateQueries(['detail-page', id]);
        }
      } else {
        const data = await community.insertComment(body, communityType);
        if (data.status === 200) {
          gtag.checkCommunityCommentInsert(body.isReply);
          queryClient.invalidateQueries(['detail-page', id]);
        }
      }
    },
    {
      onError: () => {
        alert('댓글작성 요청실패');
      },
    }
  );
};

export const useUpdateCommunity = (communityType: string) => {
  const router = useRouter();
  if (communityType === 'donation') {
    // TODO: type any 안하면 에러난다..
    return useMutation(async (donationData: any) => {
      const res: any = await community.updateDonation(donationData);
      if (res.status === 200) {
        router.push(`/community/donation/detail/${donationData.id}`);
      } else {
        alert('수정을 실패했습니다.');
      }
    });
  } else {
    return useMutation(async (boardData: any) => {
      const res: any = await community.updateBoard(boardData);
      if (res.status === 200) {
        router.push(`/community/board/detail/${boardData.uuid}`);
      } else {
        alert('수정을 실패했습니다.');
      }
    });
  }
};

export const useDeleteCommunity = (id: string, communityType: string) => {
  const router = useRouter();
  return useMutation(async () => {
    const res: any = await community.deleteCommunity(id, communityType);
    if (res.status === 200) {
      router.push(`/community/${communityType}`);
    } else {
      alert('삭제를 실패했습니다.');
    }
  });
};

export const useUpdateComment = (id: string, communityType: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (body: requestComment) => {
      // TODO 뮤테이션 콜백함수에 인자가 두개이상 들어갈 수 있으면 개선한다.
      if (body.isReply) {
        const data = await community.updateReplyComment(body, communityType);
        if (data.status === 200) {
          queryClient.invalidateQueries(['detail-page', id]);
        }
      } else {
        const data = await community.updateComment(body, communityType);
        if (data.status === 200) {
          queryClient.invalidateQueries(['detail-page', id]);
        }
      }
    },
    {
      onError: () => {
        alert('댓글수정 요청실패');
      },
    }
  );
};

export const useDeleteComment = (id: string, communityType: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (body: requestComment) => {
      // TODO 뮤테이션 콜백함수에 인자가 두개이상 들어갈 수 있으면 개선한다.
      if (body.isReply) {
        const data = await community.deleteReplyComment(
          body.requestData.uuid,
          communityType
        );
        if (data.status === 200) {
          queryClient.invalidateQueries(['detail-page', id]);
        }
      } else {
        const data = await community.deleteComment(
          body.requestData.uuid,
          communityType
        );
        if (data.status === 200) {
          queryClient.invalidateQueries(['detail-page', id]);
        }
      }
    },
    {
      onError: () => {
        alert('댓글삭제 요청실패');
      },
    }
  );
};

export const useDonationShareMutaion = (id: string, isDetail: boolean) => {
  // 프로필 조회를 이미해주기 때문에 캐싱된 데이터를 가져와서 비교한다.
  const { fetchProfile } = useAuth();
  const { data } = fetchProfile();
  const { showToast } = useToast();
  const setModal = useSetRecoilState(modalAtom);
  const profile = useRecoilValue(profileAtom);
  return useMutation(
    async (req: any) => {
      if (!profile.isLoggedIn) {
        showToast('info', `클립보드에 복사되었습니다.`);
        gtag.share('donation');
        if (isDetail) {
          copyClipBoard(`${window.location.href}`);
        } else {
          copyClipBoard(`${window.location.href}/detail/${id}`);
        }
        return;
      } else {
        setModal({
          id: 'point',
          visible: true,
        });
        showToast('info', `클립보드에 복사되었습니다.`, {
          position: 'bottom-center',
          icon: '',
          closeButton: false,
        });
        const shareUrl = await community.postShareDonation(req);
        copyClipBoard(shareUrl);
      }
    },
    {
      onError: () => {
        alert('사연공유 에러');
      },
    }
  );
};

export const fetchSelectBloodReviews = async ({
  uuId = '',
  pageParam = 0,
  keyword = '',
}) => {
  const result = await blood.getSelectBloodHomeReviews(
    uuId,
    pageParam,
    keyword
  );
  return {
    data: result,
    nextPage: result.last ? undefined : pageParam + 1,
  };
};
/**
 *
 * 특정 헌혈의집의 리스트를 출력하는 hooks이다
 * ex: 서울대입구역 센터점
 * @param uuId 헌혈의집 uuid
 * @param keyword 헌혈의집리스트에서 검색
 */
export const useBloodReviews = (uuId: string, keyword?: string) => {
  const {
    data: communitys,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    BloodKeys.selectBloodHome,
    ({ pageParam = 0 }) =>
      fetchSelectBloodReviews({
        uuId,
        pageParam,
        keyword,
      }),
    {
      getNextPageParam: (lastPage: any) => {
        if (lastPage.data.last == false) {
          return lastPage.nextPage;
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
    }
  );
  return {
    communitys,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

/**
 * 헌혈의집 리뷰 모음 커뮤니티에서 mutate 시전했을 때 이다.
 * @param communityType
 * @returns
 */
export const useBloodHomeReviewListMutaion = () => {
  const setModal = useSetRecoilState(modalAtom);
  const { lockScroll } = useBodyScrollLock();
  const queryClient = useQueryClient();
  const profile = useRecoilValue(profileAtom);
  // 프로필 조회를 이미해주기 때문에 캐싱된 데이터를 가져와서 비교한다.
  return useMutation(
    async (id: string) => {
      // 프로필 조회로 얻은 데이터가 없을시 로그인 X 유저이기때문에 리턴해서 호출을 방지한다.
      if (!profile.isLoggedIn) {
        lockScroll();
        setModal({
          id: 'unAuthorized',
          visible: true,
        });
        return;
      }
      await community.toggleLike(id, 'board');
      // TODO : 좋아요 하트만 칠해주고 조회는 하지않는다.
      queryClient.invalidateQueries(BloodKeys.reviews);
      gtag.like('board');
    },
    {
      onError: () => {
        alert('좋아요 에러');
      },
    }
  );
};
